import React, { useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import config from 'constants/config'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCalendarSidebarDatePicker from '../Filters/CalendarSidebarPicker/OfferCalendarSidebarDatePicker'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferSidebarDatePicker from '../Filters/SidebarDatePicker/OfferSidebarDatePicker'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferAirportOptions from '../Filters/Airport/OfferAirportOptions'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { pluralizeToString } from 'lib/string/pluralize'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { min } from 'lib/array/arrayUtils'
import { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import OfferDurationOptions from '../Filters/Duration/OfferDurationOptions'
import AlwaysOnPackageSelectionModal from './MobileBookingFlow/Modals/AlwaysOnPackageSelectionModal'
import AlwaysOnRoomSelectionModal from './MobileBookingFlow/Modals/AlwaysOnRoomSelectionModal'
import AlwaysOnRoomAndPackageDetailsModal from './MobileBookingFlow/Modals/AlwaysOnRoomAndPackageDetailsModal'

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.default.five};
    }
  }
`

interface Props {
  className?: string;
  onViewPackageOptions?: () => void;
  activeFilter: OfferPageFilterType;
  capacities?: Array<App.RoomCapacity>;
  durationOptions: Array<OfferDurationOption>;
  shouldShowCalendarWithoutPricing?: boolean;
}

function AlwaysOnSidebarFilters(props: Props) {
  const {
    onViewPackageOptions,
    activeFilter,
    capacities,
    durationOptions,
    shouldShowCalendarWithoutPricing,
  } = props

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const formRef = useRef<HTMLFormElement>(null)

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
  }, [dispatch, onUserAnalyticsAction])

  const filterContinue = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterContinue })
    }
  }, [dispatch])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterBack })
    }
  }, [dispatch])

  const onApply = useCallback(() => {
    filterContinue()
    onViewPackageOptions?.()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
  }, [filterContinue, onViewPackageOptions, onUserAnalyticsAction])

  const minimumDuration = useMemo(() => min(durationOptions, option => option.duration)?.duration ?? 1, [durationOptions])

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  return (
    <ModalBase
      isOpen={delayedOpen}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={`Your ${config.brandConfig.holidaySynonym}`}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              <OfferCapacityWithAgesSelectBox inSidebar />
              <OfferCalendarDatePickerSelectBox inSidebar />
              <OfferAirportSelectBox inSidebar />
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'airport' && <>
          <ModalHeader
            title="Where will you depart from?"
            subtitle="Select your preferred airport"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Continue',
              onClick: onApply,
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title="Who is going?"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && shouldShowCalendarWithoutPricing && <OfferSidebarDatePicker
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'checkin' && !shouldShowCalendarWithoutPricing && <OfferCalendarSidebarDatePicker
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'duration' && <>
          <ModalHeader
            title="How long do you want to stay?"
            subtitle={minimumDuration > 1 ? `Minimum stay for this offer is ${pluralizeToString('night', minimumDuration)}` : undefined}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <ModalContent>
              <OfferDurationOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Continue',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'roomSelection' && <AlwaysOnRoomSelectionModal
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'packageSelection' && <AlwaysOnPackageSelectionModal
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'roomDetails' && <AlwaysOnRoomAndPackageDetailsModal
          onClose={closeFilter}
        />}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  activeFilter: state.activeFilter,
  durationOptions: getDurationOptions(state, appState),
}))(AlwaysOnSidebarFilters)
